import { FilterTypes } from "../../../../types";
import { DateTime } from "luxon";
import { ATTRIBUTION_MODEL_FIELDS } from "./configuration";
/**
 * The wire contract for the looker-gateway attribution query endpoints
 * (`POST /looker/v1/query/attribution/{cross-channel,linear,overlap}`).
 *
 * These endpoints speak snake_case names derived from `AttributionModel`, so a
 * LookML rename is not an API change. Looker's own field names
 * (`explore_conversion.*`) are deliberately absent — they are the service's
 * implementation detail.
 */
/**
 * Every `AttributionModel` field and the name the API publishes it under.
 *
 * Written out rather than computed: `satisfies Record<AttributionModelFields,
 * string>` makes it exhaustive, so a field added, renamed or removed upstream
 * fails to compile here until this map is updated. `AttributionFieldName` and
 * `AttributionRow` derive from it, so the published names, the row type and the
 * runtime lookups cannot disagree.
 */
export const ATTRIBUTION_MODEL_TO_SNAKE_MAP = {
    mediaType: "media_type",
    totalImpressions: "total_impressions",
    totalCompletes: "total_completes",
    totalReach: "total_reach",
    averageFrequency: "average_frequency",
    totalClicks: "total_clicks",
    clickThroughRate: "click_through_rate",
    viewCompletionRate: "view_completion_rate",
    listenThroughRate: "listen_through_rate",
    totalConversions: "total_conversions",
    conversionRate: "conversion_rate",
    uniqueConversions: "unique_conversions",
    uniqueConversionRate: "unique_conversion_rate",
    campaignName: "campaign_name",
    advertiserName: "advertiser_name",
    congressionalDistrict: "congressional_district",
    congressionalDistrictCode: "congressional_district_code",
    creativeName: "creative_name",
    date: "date",
    week: "week",
    dayOfWeek: "day_of_week",
    dayOfMonth: "day_of_month",
    hourOfDay24: "hour_of_day24",
    hourOfDay12: "hour_of_day12",
    hourOfWeek: "hour_of_week",
    daypart: "daypart",
    deviceType: "device_type",
    subtype: "subtype",
    daysToConversion: "days_to_conversion",
    frequency: "frequency",
    metro: "metro",
    metroCode: "metro_code",
    lineitemName: "lineitem_name",
    publisherName: "publisher_name",
    creativeSize: "creative_size",
    state: "state",
    stateCode: "state_code",
    zipCode: "zip_code",
    contentLanguage: "content_language",
    month: "month",
    monthOfYear: "month_of_year",
    beaconName: "beacon_name",
    beaconId: "beacon_id",
    beaconUrl: "beacon_url",
    beaconPath: "beacon_path",
    pageCategory: "page_category",
    pageCategoryAdvertiserVertical: "page_category_advertiser_vertical",
    vin: "vin",
    make: "make",
    model: "model",
    year: "year",
    conversionType: "conversion_type",
    eventType: "event_type",
    formName: "form_name",
    buttonName: "button_name",
    contactType: "contact_type",
    cartItems: "cart_items",
    orderId: "order_id",
    campaignId: "campaign_id",
    creativeId: "creative_id",
    lineitemId: "lineitem_id",
    optimizationType: "optimization_type",
    isOptimized: "is_optimized",
    bucketDateRange: "bucket_date_range",
    mediaCost: "media_cost",
    audienceCost: "audience_cost",
    totalSpend: "total_spend",
    costPerMille: "cost_per_mille",
    costPerConversion: "cost_per_conversion",
    costPerClick: "cost_per_click",
    costPerCompletedView: "cost_per_completed_view",
    supplementalCost: "supplemental_cost",
    footfallVisitorsImpressionTime: "footfall_visitors_impression_time",
    footfallVisitsImpressionTime: "footfall_visits_impression_time",
    footfallVisitorsConversionTime: "footfall_visitors_conversion_time",
    footfallVisitsConversionTime: "footfall_visits_conversion_time",
    pageViewConversions: "page_view_conversions",
    leadConversions: "lead_conversions",
    purchaseConversions: "purchase_conversions",
    purchaseRevenue: "purchase_revenue",
    roas: "roas",
    addToCartValue: "add_to_cart_value",
    linearOnlyAverageFrequency: "linear_only_average_frequency",
    linearOnlyConversionRate: "linear_only_conversion_rate",
    linearOnlyProjectedHouseholds: "linear_only_projected_households",
    linearOnlyProjectedImpressions: "linear_only_projected_impressions",
    linearOnlyProportionReach: "linear_only_proportion_reach",
    households: "households",
    linearSample: "linear_sample",
    linearSampleMarket: "linear_sample_market",
    ottAverageFrequency: "ott_average_frequency",
    ottOnlyConversionRate: "ott_only_conversion_rate",
    ottProjectedHouseholds: "ott_projected_households",
    ottProjectedImpressions: "ott_projected_impressions",
    ottProportionReach: "ott_proportion_reach",
    overlapAverageFrequency: "overlap_average_frequency",
    overlapConversionRate: "overlap_conversion_rate",
    overlapProjectedHouseholds: "overlap_projected_households",
    overlapProjectedImpressions: "overlap_projected_impressions",
    overlapProportionReach: "overlap_proportion_reach",
    linearAdvertiserId: "linear_advertiser_id",
    linearAdvertiserName: "linear_advertiser_name",
    linearBeaconId: "linear_beacon_id",
    linearBeaconName: "linear_beacon_name",
    linearCreativeCode: "linear_creative_code",
    linearCreativeName: "linear_creative_name",
    linearDaypartName: "linear_daypart_name",
    linearImpressionDate: "linear_impression_date",
    linearImpressionHourOfWeek: "linear_impression_hour_of_week",
    linearImpressionHourOfDay12: "linear_impression_hour_of_day12",
    linearImpressionHourOfDay24: "linear_impression_hour_of_day24",
    linearImpressionMonth: "linear_impression_month",
    linearImpressionMonthOfYear: "linear_impression_month_of_year",
    linearImpressionWeek: "linear_impression_week",
    linearImpressionWeekOfYear: "linear_impression_week_of_year",
    linearImpressionYear: "linear_impression_year",
    linearMetroName: "linear_metro_name",
    linearMetroCode: "linear_metro_code",
    linearNetworkName: "linear_network_name",
    linearProgramName: "linear_program_name",
    linearDaysToConversion: "linear_days_to_conversion",
    linearFrequency: "linear_frequency",
    linearConversions: "linear_conversions",
    linearUniqueConversions: "linear_unique_conversions",
    linearUniqueConversionRate: "linear_unique_conversion_rate",
    linearConversionRate: "linear_conversion_rate",
    linearAverageFrequency: "linear_average_frequency",
    linearProjectedHouseholds: "linear_projected_households",
    linearImpressions: "linear_impressions",
    linearReachRate: "linear_reach_rate",
    linearReach: "linear_reach",
    footfallVisitDay: "footfall_visit_day",
    footfallVisitDayOfWeek: "footfall_visit_day_of_week",
    footfallCity: "footfall_city",
    footfallState: "footfall_state",
    footfallZipcode: "footfall_zipcode"
};
/**
 * Two fields mapped to the same published name would silently drop a key from
 * `AttributionRow`, and `satisfies` cannot see that, so the inversion checks it
 * at load. Shared by every `*_MODEL_TO_SNAKE_MAP` below, not just attribution's
 * — `label` names the model in the thrown message. Exported for tests.
 */
export const buildSnakeToModelMap = (modelToSnake, label = "Attribution") => {
    const map = {};
    const collisions = [];
    for (const field of Object.keys(modelToSnake)) {
        const snake = modelToSnake[field];
        if (snake in map) {
            collisions.push(`${snake} <- ${map[snake]}, ${field}`);
            continue;
        }
        map[snake] = field;
    }
    if (collisions.length > 0) {
        throw new Error(`${label} field names collide when converted to snake_case: ${collisions.join("; ")}`);
    }
    return map;
};
/** snake_case API field name -> the camelCase `AttributionModel` field. */
export const ATTRIBUTION_SNAKE_TO_MODEL_MAP = buildSnakeToModelMap(ATTRIBUTION_MODEL_TO_SNAKE_MAP);
/** Resolves an API field name, or `undefined` if it names no model field. */
export const toModelField = (name) => ATTRIBUTION_SNAKE_TO_MODEL_MAP[name];
export const toApiField = (field) => ATTRIBUTION_MODEL_TO_SNAKE_MAP[field];
/** Every attribution field name, in `AttributionModel` order. */
export const ALL_ATTRIBUTION_FIELD_NAMES = Object.keys(ATTRIBUTION_MODEL_TO_SNAKE_MAP).map((field) => ATTRIBUTION_MODEL_TO_SNAKE_MAP[field]);
export const CROSS_CHANNEL_REQUIRED_FILTERS = [
    "analysis_interval",
    "advertiser_id",
    "media_type",
    "lineitem_ids"
];
/**
 * The linear explore has no line-item or campaign filter — `creative_ids` is
 * the only campaign-level scope it accepts.
 */
export const LINEAR_REQUIRED_FILTERS = [
    "analysis_interval",
    "advertiser_id",
    "creative_ids"
];
export const OVERLAP_REQUIRED_FILTERS = [
    "analysis_interval",
    "advertiser_id",
    "creative_ids",
    "lineitem_ids"
];
/**
 * The wire contract for the looker-gateway filter-options endpoints
 * (`POST /looker/v1/query/attribution/{cross-channel,linear,overlap}/filter-options`).
 *
 * Same split as the attribution contract above: `ConversionFilterSuggestsModel`
 * and `LinearConversionFilterSuggestsModel` stay camelCase in `index.ts`; these
 * maps project them to the snake_case names the endpoints publish. Looker's own
 * field names (`conversion_filter_suggests.*`) are deliberately absent here —
 * they are the service's implementation detail.
 */
/**
 * Every `ConversionFilterSuggestsModel` field and the name the API publishes it
 * under. Written out rather than computed: `satisfies Record<keyof
 * ConversionFilterSuggestsModel, string>` makes it exhaustive, so a field added,
 * renamed or removed upstream fails to compile here until this map is updated.
 */
export const CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP = {
    advertiserName: "advertiser_name",
    advertiserId: "advertiser_id",
    campaignName: "campaign_name",
    campaignId: "campaign_id",
    lineitemId: "lineitem_id",
    beaconName: "beacon_name",
    beaconId: "beacon_id",
    pixelType: "pixel_type",
    beaconStatus: "beacon_status"
};
/** snake_case API field name -> the camelCase `ConversionFilterSuggestsModel` field. */
export const CONVERSION_FILTER_SUGGESTS_SNAKE_TO_MODEL_MAP = buildSnakeToModelMap(CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP, "Conversion filter suggests");
/** Every conversion-filter-suggests field name, in `ConversionFilterSuggestsModel` order. */
export const ALL_CONVERSION_FILTER_SUGGESTS_FIELD_NAMES = Object.keys(CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP).map((field) => CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP[field]);
export const CROSS_CHANNEL_OPTIONS_REQUIRED_FILTERS = [
    "analysis_interval"
];
/**
 * Every `LinearConversionFilterSuggestsModel` field and the name the API
 * publishes it under.
 */
export const LINEAR_CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP = {
    advertiserId: "advertiser_id",
    advertiserName: "advertiser_name",
    creativeId: "creative_id",
    creativeName: "creative_name"
};
/** snake_case API field name -> the camelCase `LinearConversionFilterSuggestsModel` field. */
export const LINEAR_CONVERSION_FILTER_SUGGESTS_SNAKE_TO_MODEL_MAP = buildSnakeToModelMap(LINEAR_CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP, "Linear conversion filter suggests");
/** Every linear-filter-options field name, in `LinearConversionFilterSuggestsModel` order. */
export const ALL_LINEAR_CONVERSION_FILTER_SUGGESTS_FIELD_NAMES = Object.keys(LINEAR_CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP).map((field) => LINEAR_CONVERSION_FILTER_SUGGESTS_MODEL_TO_SNAKE_MAP[field]);
export const LINEAR_OPTIONS_REQUIRED_FILTERS = [
    "analysis_interval"
];
/**
 * The report types a query is scoped to: validated, de-duplicated and sorted so
 * one logical query is one cache key however the UI ordered the selection.
 *
 * Shared by the Looker query builder and the endpoint request builder, so the
 * two refuse the same queries rather than drifting into two answers.
 */
export const resolveAttributionReportTypes = (where) => {
    const value = where.find((filter) => filter.field === "reportType")?.value;
    const reportTypes = Array.from(new Set(Array.isArray(value) ? value : value ? [value] : [])).sort();
    if (reportTypes.length === 0) {
        throw new Error("You must specify a reportType filter");
    }
    if (reportTypes.includes("OVERLAP") && reportTypes.length > 1) {
        throw new Error("OVERLAP cannot be combined with other report types");
    }
    if (reportTypes.includes("LINEAR") &&
        reportTypes.some((type) => type === "VIDEO" || type === "AUDIO" || type === "DISPLAY")) {
        throw new Error("LINEAR cannot be combined with digital report types (VIDEO, AUDIO, DISPLAY)");
    }
    return reportTypes;
};
/** OVERLAP and LINEAR each have a dedicated explore; the digital channels share one. */
export const attributionEndpointFor = (reportTypes) => reportTypes.includes("OVERLAP")
    ? "/attribution/overlap"
    : reportTypes.includes("LINEAR")
        ? "/attribution/linear"
        : "/attribution/cross-channel";
/**
 * `satisfies` rather than a type annotation, so a value that is not a published
 * filter name — or a key that is not a real model filter — fails to compile
 * rather than earning a 400.
 */
const CROSS_CHANNEL_FILTER_NAMES = {
    dates: "analysis_interval",
    advertiserIds: "advertiser_id",
    lineitemIds: "lineitem_ids",
    pixelIds: "beacon_ids",
    metroIds: "metro_codes",
    reportType: "media_type",
    lookbackDays: "lookback_window",
    pixelType: "pixel_type",
    queryLabel: "query_label",
    useExpandedTable: "use_expanded_table"
};
const LINEAR_FILTER_NAMES = {
    dates: "analysis_interval",
    advertiserIds: "advertiser_id",
    commercialIds: "creative_ids",
    pixelIds: "beacon_ids",
    metroIds: "metro_codes",
    lookbackDays: "lookback_window",
    pixelType: "pixel_type",
    queryLabel: "query_label"
};
const OVERLAP_FILTER_NAMES = {
    dates: "analysis_interval",
    advertiserIds: "advertiser_id",
    lineitemIds: "lineitem_ids",
    commercialIds: "creative_ids",
    pixelIds: "beacon_ids",
    metroIds: "metro_codes",
    lookbackDays: "lookback_window",
    pixelType: "pixel_type",
    queryLabel: "query_label"
};
const FILTER_NAMES = {
    "/attribution/cross-channel": CROSS_CHANNEL_FILTER_NAMES,
    "/attribution/linear": LINEAR_FILTER_NAMES,
    "/attribution/overlap": OVERLAP_FILTER_NAMES
};
/**
 * Filters the Looker path silently ignores on a given explore, dropped here for
 * the same reason rather than sent and refused. Each is a filter the dashboard
 * can genuinely set, so leaving it in would turn a tile that renders today into
 * a 400 the moment the flag is on.
 *
 * - `lineitemIds` on linear: the explore has no line-item filter, and Smithers'
 *   own validity check requires line items for every report type, so this one is
 *   on the hot path rather than an edge case.
 * - `commercialIds` on cross-channel: no `creative_ids` filter there, and the
 *   commercial selection survives a channel switch in the dashboard's state.
 * - `reportType` off cross-channel: the explore is the channel.
 * - `useExpandedTable` off cross-channel: the expanded table is static-web only.
 */
const DROPPED_FILTERS = {
    "/attribution/cross-channel": new Set(["commercialIds"]),
    "/attribution/linear": new Set(["lineitemIds", "reportType", "useExpandedTable"]),
    "/attribution/overlap": new Set(["reportType", "useExpandedTable"])
};
/**
 * Filters with no counterpart on any endpoint. Unreachable from the tile path
 * today — the dashboard never sets either — so these are tripwires: a later
 * change that starts sending them fails loudly instead of quietly querying the
 * wrong thing.
 */
const UNSUPPORTED_FILTERS = new Set([
    "relativeDate",
    "bucketDateRangeLength"
]);
/**
 * The interval as the API takes it: dashes, and **inclusive at both ends**.
 *
 * Deliberately not `parseDateFilter`, which is wrong here twice over — it emits
 * slashes, and it adds a day because Looker's filter is end-exclusive. The
 * service applies that shift itself, so forwarding a pre-shifted end date
 * queries a day too far and returns plausible numbers rather than an error.
 *
 * Generic rather than pinned to `AttributionFilters`: only `type`/`value` are
 * read, neither of which depends on the filter set's field union, so this is
 * shared with the filter-options translators below.
 */
export const toAnalysisInterval = (filter) => {
    if (filter.type !== FilterTypes.BETWEEN || !Array.isArray(filter.value)) {
        throw new Error("dates must be a date range");
    }
    const [start, end] = filter.value;
    if (!(start instanceof Date) || !(end instanceof Date)) {
        throw new Error("dates must contain two dates when filtering for a date range");
    }
    return `${DateTime.fromJSDate(start).toFormat("yyyy-MM-dd")} to ${DateTime.fromJSDate(end).toFormat("yyyy-MM-dd")}`;
};
/**
 * The dates filter every filter-options handler requires, parsed straight to
 * the service's `analysis_interval` format. Throws the same message the
 * Looker path already throws when it's missing, so neither transport earns a
 * new failure mode. Generic for the same reason as `toAnalysisInterval`.
 */
export const requireAnalysisInterval = (where) => {
    const dateFilter = where?.find((filter) => filter.field === "dates");
    if (!dateFilter) {
        throw new Error("Dates filter is required");
    }
    return toAnalysisInterval(dateFilter);
};
/**
 * The advertiser id every filter-options handler but Advertiser's own
 * requires — it produces the advertiser list, so it has none to filter by.
 */
export const requireAdvertiserIdFilter = (where) => {
    const advertiserIdFilter = where?.find((filter) => filter.field === "advertiser")?.value;
    if (!advertiserIdFilter) {
        throw new Error("Advertiser ID filter is required");
    }
    return advertiserIdFilter;
};
/**
 * Mirrors `parseStringFilter`: EQ takes a string, IN takes a list, and anything
 * else is refused. Without the operator check a filter the Looker path rejects
 * would be serialised into a valid-looking request here, so turning the flag on
 * would run a different query rather than reproduce the same error.
 */
const toStringValue = (filter) => {
    switch (filter.type) {
        case FilterTypes.EQ:
            if (typeof filter.value !== "string") {
                throw new Error(`Filter ${filter.field} must be a string`);
            }
            return filter.value;
        case FilterTypes.IN:
            if (!Array.isArray(filter.value)) {
                throw new Error(`Filter ${filter.field} must be an array of strings`);
            }
            return filter.value.join(",");
        default:
            throw new Error(`Filter ${filter.type} is not supported for the field ${filter.field}`);
    }
};
/** Mirrors `parseNumberFilter`, for the same reason. */
const toNumberValue = (filter) => {
    switch (filter.type) {
        case FilterTypes.EQ:
            if (typeof filter.value !== "number") {
                throw new Error(`Filter ${filter.field} must be a number`);
            }
            return filter.value.toString();
        case FilterTypes.IN:
            if (!Array.isArray(filter.value)) {
                throw new Error(`Filter ${filter.field} must be an array of numbers`);
            }
            return filter.value.join(",");
        default:
            throw new Error(`Filter ${filter.type} is not supported for the field ${filter.field}`);
    }
};
/** The API value for one model filter, or `undefined` to omit the key. */
const toFilterValue = (field, filter, { reportTypes, pixelType }) => {
    switch (field) {
        case "dates":
            return toAnalysisInterval(filter);
        // Already sorted and digital-only by the time it gets here; the service
        // sorts and de-duplicates again, so one logical query is one Looker cache
        // entry however it arrived.
        case "reportType":
            // Value shape is already settled by resolveAttributionReportTypes; the
            // operator is not, and the Looker path parses this one as a string filter.
            toStringValue(filter);
            return reportTypes.join(",");
        // An empty selection means "every pixel". The Looker path expresses that by
        // pushing an empty filter that becomes the literal NULL; the endpoint
        // applies the same NULL when the key is absent.
        case "pixelIds": {
            const value = toStringValue(filter);
            return value === "" ? undefined : value;
        }
        // Only ever emitted alongside a STATIC pixel type on the digital explore.
        // mad-sdk ignores it everywhere else, and the endpoint refuses it there.
        // Carries UI intent rather than a Looker value, so the Looker path never
        // parses it and neither does this — only its truthiness is read.
        case "useExpandedTable":
            return filter.value && pixelType === "STATIC" ? "true" : undefined;
        case "lookbackDays":
            return toNumberValue(filter);
        default:
            return toStringValue(filter);
    }
};
/**
 * Every field asked for, plus the companion id of any dimension that declares
 * one — the id is what separates rows that would otherwise group under a shared
 * name. The endpoints return exactly what they are given, so this is the only
 * place the expansion happens.
 */
export const withCompanionFields = (fields) => {
    const expanded = new Set();
    for (const field of fields) {
        expanded.add(field);
        const companion = ATTRIBUTION_MODEL_FIELDS[field]?.fetchWithField;
        if (companion) {
            expanded.add(companion);
        }
    }
    return Array.from(expanded);
};
/**
 * Translates the filters the Insights dashboard hands `findItems` into the
 * request the endpoint takes. Six of the names are not snake_case transforms of
 * what the dashboard emits, and dates, booleans and the id arrays all change
 * shape, so this is where the migration's real work is.
 */
export const toAttributionApiRequest = (filters) => {
    const where = (filters.where ?? []);
    const reportTypes = resolveAttributionReportTypes(where);
    const endpoint = attributionEndpointFor(reportTypes);
    const fields = withCompanionFields(filters.fields ?? []).map(toApiField);
    if (fields.length === 0) {
        throw new Error("You must specify at least one field to query");
    }
    const names = FILTER_NAMES[endpoint];
    const dropped = DROPPED_FILTERS[endpoint];
    const context = {
        reportTypes,
        pixelType: where.find((filter) => filter.field === "pixelType")?.value
    };
    const apiFilters = {};
    for (const filter of where) {
        const field = filter.field;
        if (UNSUPPORTED_FILTERS.has(field)) {
            throw new Error(`${field} is not supported by the reporting API`);
        }
        if (dropped.has(field)) {
            continue;
        }
        const name = names[field];
        if (!name) {
            throw new Error(`${field} has no reporting API filter name`);
        }
        const value = toFilterValue(field, filter, context);
        if (value !== undefined) {
            apiFilters[name] = value;
        }
    }
    return {
        endpoint,
        request: { fields, filters: apiFilters, limit: -1 }
    };
};
/**
 * Response rows back to the camelCase model the dashboard reads. Every name the
 * row carries is mapped, not only the ones the caller listed, because the
 * request asked for the companion ids too.
 */
export const toAttributionModelRows = (rows) => rows.map((row) => {
    const model = {};
    for (const [name, value] of Object.entries(row)) {
        const field = toModelField(name);
        if (field) {
            model[field] = value;
        }
    }
    return model;
});
//# sourceMappingURL=api.js.map